"use client";

import React, { useState, useEffect, useMemo } from "react";

import * as Icon from "@phosphor-icons/react/dist/ssr";
import { useLocation, useNavigate } from "react-router-dom";
import Product from "../../../components/Product/Product";
import productData from "../../../data/Product.json";
import useLoginPopup from "../../../store/useLoginPopup";
import useMenuMobile from "../../../store/useMenuMobile";
import { useModalCartContext } from "../../../context/ModalCartContext";
import { useModalWishlistContext } from "../../../context/ModalWishlistContext";
import { useModalSearchContext } from "../../../context/ModalSearchContext";
import { useCart } from "../../../context/CartContext";

interface Props {
  props: string;
}

const MenuFurniture: React.FC<Props> = ({ props }) => {
  const location = useLocation();
  const pathname = location.pathname;
  
  const [openSubNavMobile, setOpenSubNavMobile] = useState<number | null>(null);
  const { openModalCart } = useModalCartContext();
  const { cartState } = useCart();
  const { openModalWishlist } = useModalWishlistContext();
  const { openModalSearch } = useModalSearchContext();
  const [searchKeyword, setSearchKeyword] = useState("");
  const [MenuOpen, setMenuOpen] = useState(false);

  const router = useNavigate();

  const handleSearch = (value: string) => {
    window.location.href = `/search-result?query=${value}`;
    setSearchKeyword("");
  };

  const handleOpenSubNavMobile = (index: number) => {
    setOpenSubNavMobile(openSubNavMobile === index ? null : index);
  };

  const [fixedHeader, setFixedHeader] = useState(false);
  const [lastScrollPosition, setLastScrollPosition] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setFixedHeader(scrollPosition > 0 && scrollPosition < lastScrollPosition);
      setLastScrollPosition(scrollPosition);
    };

    // Gắn sự kiện cuộn khi component được mount
    window.addEventListener("scroll", handleScroll);

    // Hủy sự kiện khi component bị unmount
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [lastScrollPosition]);

  // Add a state to track cart changes
  const [cartCount, setCartCount] = useState(0);

  // Update cart count when component mounts and when localStorage changes
  useEffect(() => {
    const updateCartCount = () => {
      const cartItemsJSON = localStorage.getItem("cartItems");
      const cartItems: { id: string | number; quantity: number }[] = cartItemsJSON
        ? JSON.parse(cartItemsJSON)
        : [];
      setCartCount(cartItems.length);
    };

    // Initial update
    updateCartCount();

    // Listen for storage events (when cart is updated from other components)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "cartItems") {
        updateCartCount();
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // Also check periodically for changes (fallback for same-tab updates)
    const interval = setInterval(updateCartCount, 1000);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  return (
    <>
      <div
        className={`header-menu style-one relative w-full pt-1 pb-3 max-sm:py-2 ${props}`}
      >
        <div className="w-[96%] mx-auto h-full">
          <div className="header-main flex items-center justify-between h-full max-md:py-5">
            <button
              className="menu-mobile-icon lg:hidden flex items-center"
              onClick={() => setMenuOpen(true)}
            >
              <Icon.List size={36} color="#ff4370" weight="bold" className="transition-transform duration-200" />
            </button>
            <div className="relative logo-light-effect">
              <a href="/">
                <img
                  src="/logo.png"
                  width={700}
                  height={700}
                  alt="bg-img"
                  className="object-cover relative z-10"
                  style={{
                    width: "121px",
                  }}
                />
              </a>
            </div>
            <div className="form-search flex items-center relative max-lg:hidden z-[1] w-[34%]">
              <input
                type="text"
                placeholder="Recherche..."
                className="caption1 w-full h-12 pl-4 md:pr-[150px] pr-32 rounded-xl border border-line"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                onKeyDown={(e) =>
                  e.key === "Enter" && handleSearch(searchKeyword)
                }
              />
              <button
                className="button-main bg-pink hover:bg-greenprimary absolute top-1 bottom-1 right-1 flex items-center justify-center px-4 rounded-lg"
                onClick={() => handleSearch(searchKeyword)}
              >
                <Icon.MagnifyingGlass size={16} className="text-white mr-2" />
                Rechercher
              </button>
            </div>
            <div className="right flex items-center gap-12 relative z-[1]">
              <div className="menu-main h-full xl:w-full flex items-center justify-center max-lg:hidden">
                <ul className="flex items-center gap-8 h-full">
                  <li className="h-full relative">
                    <a
                      href="/"
                      className={`text-button-uppercase text-pink duration-300 h-full flex items-center justify-center text-sm gap-1 
                        ${pathname === "/" || pathname === "" ? "active" : ""}`}
                      style={{
                        fontSize: "12px",
                      }}
                    >
                      ACCUEIL
                    </a>
                  </li>
                  <li className="h-full">
                    <a
                      href="/shop"
                      className={`text-button-uppercase text-pink duration-300 h-full flex items-center justify-center text-sm 
                        ${pathname.includes("/shop") ? "active" : ""}`}
                      style={{
                        fontSize: "12px",
                      }}
                    >
                      BOUTIQUE
                    </a>
                  </li>
                  <li className="h-full">
                    <a
                      href="/pages/about"
                      className={`text-button-uppercase text-pink duration-300 h-full flex items-center justify-center text-sm 
                        ${pathname.includes("/pages/about") ? "active" : ""}`}
                      style={{
                        fontSize: "12px",
                      }}
                    >
                      nous connaître
                    </a>
                  </li>

                  <li className="h-full">
                    <a
                      href="/pages/contact"
                      className={`text-button-uppercase text-pink duration-300 h-full flex items-center justify-center text-sm 
                        ${pathname.includes("/pages/contact") ? "active" : ""}`}
                      style={{
                        fontSize: "12px",
                      }}
                    >
                      Contactez-Nous
                    </a>
                  </li>
                </ul>
              </div>
              <div className="list-action flex items-center gap-4">
                <div
                  className="cart-icon flex items-center relative cursor-pointer"
                  onClick={openModalCart}
                >
                  <Icon.Handbag size={24} color="black" />
                  <span className="quantity cart-quantity absolute top-[-5px] right-[-9px] text-xs text-white bg-black w-4 h-4 flex items-center justify-center rounded-full">
                    {cartCount}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div id="menu-mobile" className={`relative ${MenuOpen ? "open" : ""}`} style={{ zIndex: 400 }}>
        {MenuOpen && (
          <button
            type="button"
            className="menu-mobile-backdrop fixed inset-0 z-0 cursor-default border-0 bg-black/50 p-0 lg:hidden"
            aria-label="Fermer le menu"
            onClick={() => setMenuOpen(false)}
          />
        )}
        <div className="menu-container fixed left-0 top-0 z-[1] h-full w-[80vw] max-w-xs bg-white shadow-2xl">
          <div className="container h-full">
            <div className="menu-main h-full overflow-hidden">
              <div className="heading py-2 relative flex items-center justify-center">
                <button
                  className="close-menu-mobile-btn absolute left-0 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-surface flex items-center justify-center"
                  onClick={() => setMenuOpen(false)}
                >
                  <Icon.X size={14} />
                </button>
                <a
                  href={"/"}
                  className="logo text-3xl font-semibold text-center logo-light-effect"
                >
                  <img
                    src="/logo.png"
                    width={700}
                    height={700}
                    alt="bg-img"
                    className="object-cover relative z-10"
                    style={{
                      width: "121px",
                    }}
                  />
                </a>
              </div>
              <div className="list-nav mt-6">
                {/* Mobile Search Box */}
                <div className="form-search relative mb-6">
                  <input
                    type="text"
                    placeholder="Recherche..."
                    className="caption1 w-full h-12 pl-4 pr-12 rounded-xl border border-line"
                    value={searchKeyword}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                    onKeyDown={(e) =>
                      e.key === "Enter" && handleSearch(searchKeyword)
                    }
                  />
                  <button
                    className="button-main bg-pink hover:bg-greenprimary absolute top-1 bottom-1 right-1 flex items-center justify-center px-3 rounded-lg"
                    onClick={() => {
                      handleSearch(searchKeyword);
                      setMenuOpen(false);
                    }}
                  >
                    <Icon.MagnifyingGlass size={16} className="text-white" />
                  </button>
                </div>
                
                <ul className="space-y-4">
                  {/*<li
                    className={`${openSubNavMobile === 1 ? "open" : ""}`}
                    onClick={() => handleOpenSubNavMobile(1)}
                  >
                    <a
                      href={"#!"}
                      className={`text-xl font-semibold flex items-center justify-between`}
                    >
                      Demo
                      <span className="text-right">
                        <Icon.CaretRight size={20} />
                      </span>
                    </a>
                    <div className="sub-nav-mobile">
                      <div
                        className="back-btn flex items-center gap-3"
                        onClick={() => handleOpenSubNavMobile(1)}
                      >
                        <Icon.CaretLeft />
                        Back
                      </div>
                      <div className="list-nav-item w-full grid grid-cols-2 pt-2 pb-6">
                        <ul>
                          <li>
                            <a
                              href="/"
                              className={`nav-item-mobile text-secondary duration-300 ${
                                pathname === "/" ? "active" : ""
                              }`}
                            >
                              Home Fashion 1
                            </a>
                          </li>
                          <li>
                            <a
                              href="/homepages/fashion2"
                              className={`nav-item-mobile text-secondary duration-300 ${
                                pathname === "/homepages/fashion2"
                                  ? "active"
                                  : ""
                              }`}
                            >
                              Home Fashion 2
                            </a>
                          </li>
                          <li>
                            <a
                              href="/homepages/fashion3"
                              className={`nav-item-mobile text-secondary duration-300 ${
                                pathname === "/homepages/fashion3"
                                  ? "active"
                                  : ""
                              }`}
                            >
                              Home Fashion 3
                            </a>
                          </li>
                          <li>
                            <a
                              href="/homepages/fashion4"
                              className={`nav-item-mobile text-secondary duration-300 ${
                                pathname === "/homepages/fashion4"
                                  ? "active"
                                  : ""
                              }`}
                            >
                              Home Fashion 4
                            </a>
                          </li>
                          <li>
                            <a
                              href="/homepages/fashion5"
                              className={`nav-item-mobile text-secondary duration-300 ${
                                pathname === "/homepages/fashion5"
                                  ? "active"
                                  : ""
                              }`}
                            >
                              Home Fashion 5
                            </a>
                          </li>
                          <li>
                            <a
                              href="/homepages/fashion6"
                              className={`nav-item-mobile text-secondary duration-300 ${
                                pathname === "/homepages/fashion6"
                                  ? "active"
                                  : ""
                              }`}
                            >
                              Home Fashion 6
                            </a>
                          </li>
                          <li>
                            <a
                              href="/homepages/fashion7"
                              className={`nav-item-mobile text-secondary duration-300 ${
                                pathname === "/homepages/fashion7"
                                  ? "active"
                                  : ""
                              }`}
                            >
                              Home Fashion 7
                            </a>
                          </li>
                          <li>
                            <a
                              href="/homepages/fashion8"
                              className={`nav-item-mobile text-secondary duration-300 ${
                                pathname === "/homepages/fashion8"
                                  ? "active"
                                  : ""
                              }`}
                            >
                              Home Fashion 8
                            </a>
                          </li>
                          <li>
                            <a
                              href="/homepages/fashion9"
                              className={`nav-item-mobile text-secondary duration-300 ${
                                pathname === "/homepages/fashion9"
                                  ? "active"
                                  : ""
                              }`}
                            >
                              Home Fashion 9
                            </a>
                          </li>
                          <li>
                            <a
                              href="/homepages/fashion10"
                              className={`nav-item-mobile text-secondary duration-300 ${
                                pathname === "/homepages/fashion10"
                                  ? "active"
                                  : ""
                              }`}
                            >
                              Home Fashion 10
                            </a>
                          </li>
                          <li>
                            <a
                              href="/homepages/fashion11"
                              className={`nav-item-mobile text-secondary duration-300 ${
                                pathname === "/homepages/fashion11"
                                  ? "active"
                                  : ""
                              }`}
                            >
                              Home Fashion 11
                            </a>
                          </li>
                        </ul>
                        <ul>
                          <li>
                            <a
                              href="/homepages/underwear"
                              className={`nav-item-mobile text-secondary duration-300 ${
                                pathname === "/homepages/underwear"
                                  ? "active"
                                  : ""
                              }`}
                            >
                              Home Underwear
                            </a>
                          </li>
                          <li>
                            <a
                              href="/homepages/cosmetic1"
                              className={`nav-item-mobile text-secondary duration-300 ${
                                pathname === "/homepages/cosmetic1"
                                  ? "active"
                                  : ""
                              }`}
                            >
                              Home Cosmetic 1
                            </a>
                          </li>
                          <li>
                            <a
                              href="/homepages/cosmetic2"
                              className={`nav-item-mobile text-secondary duration-300 ${
                                pathname === "/homepages/cosmetic2"
                                  ? "active"
                                  : ""
                              }`}
                            >
                              Home Cosmetic 2
                            </a>
                          </li>
                          <li>
                            <a
                              href="/homepages/cosmetic3"
                              className={`nav-item-mobile text-secondary duration-300 ${
                                pathname === "/homepages/cosmetic3"
                                  ? "active"
                                  : ""
                              }`}
                            >
                              Home Cosmetic 3
                            </a>
                          </li>
                          <li>
                            <a
                              href="/homepages/pet"
                              className={`nav-item-mobile text-secondary duration-300 ${
                                pathname === "/homepages/pet" ? "active" : ""
                              }`}
                            >
                              Home Pet Store
                            </a>
                          </li>
                          <li>
                            <a
                              href="/homepages/jewelry"
                              className={`nav-item-mobile text-secondary duration-300 ${
                                pathname === "/homepages/jewelry"
                                  ? "active"
                                  : ""
                              }`}
                            >
                              Home Jewelry
                            </a>
                          </li>
                          <li>
                            <a
                              href="/homepages/furniture"
                              className={`nav-item-mobile text-secondary duration-300 ${
                                pathname === "/homepages/furniture"
                                  ? "active"
                                  : ""
                              }`}
                            >
                              Home Furniture
                            </a>
                          </li>
                          <li>
                            <a
                              href="/homepages/watch"
                              className={`nav-item-mobile text-secondary duration-300 ${
                                pathname === "/homepages/watch" ? "active" : ""
                              }`}
                            >
                              Home Watch
                            </a>
                          </li>
                          <li>
                            <a
                              href="/homepages/toys"
                              className={`nav-item-mobile text-secondary duration-300 ${
                                pathname === "/homepages/toys" ? "active" : ""
                              }`}
                            >
                              Home Toys Kid
                            </a>
                          </li>
                          <li>
                            <a
                              href="/homepages/yoga"
                              className={`nav-item-mobile text-secondary duration-300 ${
                                pathname === "/homepages/yoga" ? "active" : ""
                              }`}
                            >
                              Home Yoga
                            </a>
                          </li>
                          <li>
                            <a
                              href="/homepages/organic"
                              className={`nav-item-mobile text-secondary duration-300 ${
                                pathname === "/homepages/organic"
                                  ? "active"
                                  : ""
                              }`}
                            >
                              Home Organic
                            </a>
                          </li>
                          <li>
                            <a
                              href="/homepages/marketplace"
                              className={`nav-item-mobile text-secondary duration-300 ${
                                pathname === "/homepages/marketplace"
                                  ? "active"
                                  : ""
                              }`}
                            >
                              Home Marketplace
                            </a>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </li>*/}
                  <li className="mb-3 relative">
                    <a
                      href="/"
                      className={`text-xl font-semibold flex items-center gap-3 text-pink pb-3 text-left`}
                    >
                      <Icon.House size={22} color="#ff4370" />
                      HOME
                    </a>
                  </li>
                  <li className="mb-3">
                    <a
                      href="/shop"
                      className={`text-xl font-semibold flex items-center gap-3 text-pink pb-3 text-left`}
                    >
                      <Icon.Storefront size={22} color="#ff4370" />
                      BOUTIQUE
                    </a>
                  </li>
                  
                  <li className="mb-3 relative">
                    <a
                      href="/pages/about"
                      className={`text-xl font-semibold flex items-center gap-3 text-pink pb-3 text-left`}
                    >
                      <Icon.Info size={22} color="#ff4370" />
                      About Us
                    </a>
                  </li>
                  <li className="mb-3">
                    <a
                      href="/pages/contact"
                      className={`text-xl font-semibold flex items-center gap-3 text-pink pb-3 text-left`}
                    >
                      <Icon.Phone size={22} color="#ff4370" />
                      Contact US
                    </a>
                  </li>
                </ul>
              </div>
              <button
                className="mt-8 mb-4 w-[90%] mx-auto block bg-pink text-white font-bold flex items-center justify-center gap-2 py-3 rounded-xl shadow-lg text-lg"
                onClick={() => { openModalCart(); setMenuOpen(false); }}
              >
                <Icon.Handbag size={24} color="white" />
                Voir le panier
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MenuFurniture;
